import React, { useState, useEffect } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';

import classNames from 'classnames/bind';
import { keyboardClick, useDictionary } from 'lib/utils';

import styles from './Modal.module.scss';
import { ModalProps } from './Types';
import Icon from '../Icon';
import Button from '../Button/Button';

/**
 * Classnames binding for the Modal component.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);
const defaultLauncherTag = 'span';

/*
    <!--
        <Modal> expects following parameters:

        title - Title of the Modal dialog
        launcherText? - Optional Text for Modal launcher element - not needed when Modal launch state is controlled by component calling the Modal
        launcherTag? - Optional Tag for the launcher element, span | button
        width? - Optional string value for the Modal width, default to auto            
        openOnLaunch? - Optional boolean flag to show Modal on page load - Default False
        showFooter? - Optional boolean flag to show Dialog footer - Default True
        closeOnClick? - Optional boolean flag to close Dialog when use clicks outside modal area - Default True
        closeOnEsc? - Optional boolean flag to close Dialog when ESC key is pressed - Default True
        opened? - This is used when the dialog state is controlled by the caller component e.g. ExternalLinksWarningModal
        closeButtonLabel?: Label for the close button - if not provided, default value will be used from the dictionary
        okButtonLabel?: Label for the optional OK button - OK button will only be displayed if the value of OK button is provided
        onOpenCallback? - Optional callback to handle open event if needed
        onCloseCallback? - Optional callback to handle close event if needed
        onOKCallback? - Optional callback to handle OK event if needed
        openWithReturn? - Optional setting of pressing enter/return on the keyboard opens the modal - Default False
        openWithSpacebar? - Optional setting of pressing the spacebar on the keyboard opens the modal - Default False

        See example of a <Modal> call below:
        
        <Modal title="Modal title goes here..." launcherText='Open Modal...'>
          <h2>This is a Modal</h2>
          <p>You can put any content or components inside it.</p>
          <p>
            Disclaimer: Policies and procedures designated with a - are pending approval by the Department of Human
            Services (DHS) and are not currently approved for use for the Medical Assistance product. Please refer to
            the UPMC Health Plan website frequently for updates on approval.
          </p>
        </Modal>
*/

/**
 * A Front-end only implementation of the Modal component.
 *
 * @example
 * ```tsx
 * <Modal title="Modal Title">...</Modal>
 * ```
 *
 * @param props - Modal properties.
 * @returns A JSX element.
 */
export const Modal: React.FC<ModalProps> = (propsWithoutDefaults) => {
  const props = {
    launcherTag: propsWithoutDefaults.launcherTag ?? defaultLauncherTag,
    width: propsWithoutDefaults.width ?? 'auto',
    ...propsWithoutDefaults,
  };

  const {
    className,
    title,
    launcherText,
    launcherTag,
    children,
    openOnLaunch,
    showFooter,
    closeOnEsc,
    closeOnClick,
    width,
    closeButtonLabel,
    okButtonLabel,
    opened,
    isMobile,
    onOKCallback,
    onCloseCallback,
    onOpenCallback,
    tabIndex,
    openWithReturn,
    openWithSpacebar,
  } = props;

  const [showModal, setShowModal] = useState(openOnLaunch || opened || false);
  const defaultCloseLabel = useDictionary('Close');

  const closeLabel = closeButtonLabel || defaultCloseLabel;

  const contentStyle = {
    width: isMobile ? '100%' : width || 'auto', // Use the provided width, or default to 'auto' if not provided
  };

  // Effect to add event listener when component mounts
  useEffect(() => {
    if (closeOnEsc) {
      document.addEventListener('keydown', handleKeyPress);
    } else {
      document.removeEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const handleKeyPress = (event: KeyboardEvent) => {
    if (closeOnEsc && event.key === 'Escape') {
      closeModal();
    }
  };

  const onOKButtonClick = () => {
    closeModal();
    onOKCallback && onOKCallback();
  };

  const closeModal = () => {
    setShowModal(false);
    onCloseCallback && onCloseCallback();
  };

  const openModal = () => {
    setShowModal(true);
    onOpenCallback && onOpenCallback();
  };

  /** Combined className string for the underlying label element. */
  const classes: string = cx(
    {
      modal: true,
      modalOverlay: true,
    },
    className
  );

  return (
    <>
      <a
        onClick={openModal}
        onKeyDown={(e) => keyboardClick(e, openModal, openWithReturn, openWithSpacebar)}
        className={cx('modal-launcher')}
        tabIndex={tabIndex ?? -1}
      >
        <Text field={{ value: launcherText }} tag={launcherTag || defaultLauncherTag} encode={false} />
      </a>

      <dialog open={showModal || opened} className={cx('modal-dialog')}>
        <div className={classes} onClick={() => closeOnClick && closeModal()}>
          <div
            className={cx('modalContent', { 'modalContent-mobile': isMobile })}
            style={contentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cx('modalHeader', { 'modalHeader-mobile': isMobile })}>
              <Text
                field={{ value: title }}
                className={cx('text-purple-60', 'font-style-lg', 'modalTitle')}
                tag="div"
                encode={false}
              />
              <button className={styles.closeIcon} onClick={closeModal} title={closeLabel}>
                <Icon variant="close" />
              </button>
            </div>

            <div className={cx('modalBody', { 'modalBody-mobile': isMobile })}>{children}</div>

            {showFooter && (
              <div className={cx('modalFooter', { 'modalFooter-mobile': isMobile })}>
                {okButtonLabel && (
                  <Button type="button" onClick={onOKButtonClick} className={cx('okButton')}>
                    <Text field={{ value: okButtonLabel }} encode={false} />
                  </Button>
                )}

                <Button
                  type="button"
                  onClick={closeModal}
                  className={cx('closeButton')}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#9c258b',
                    border: '1px solid #9c258b',
                  }}
                >
                  <Text field={{ value: closeLabel }} encode={false} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </dialog>
    </>
  );
};

Modal.displayName = 'Modal';

export default Modal;
